import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Zap, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, hasModule } from "@/lib/auth";
import { fmtMoney } from "@/lib/format";

/** Dashboard tile: rent & utility bills overdue / due in the next 7 days. Hidden without the bills module. */
export function BillsDueTile() {
  const { user } = useAuth();
  const enabled = hasModule(user, "bills");
  const { data } = useQuery({ queryKey: ["bills-summary"], queryFn: () => api.billsSummary(7), enabled });
  if (!enabled || !data || data.bills_count === 0) return null;
  const overBudget = data.budget_alert;
  const overdue = data.overdue_count > 0;
  const due = data.due_soon_count > 0;
  const palette = overdue || overBudget ? "bg-red-600 border-red-700 text-white" : due ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-900";
  const headline = overdue ? `${data.overdue_count} overdue · ${fmtMoney(data.overdue_total)}` : due ? `${data.due_soon_count} due · ${fmtMoney(data.due_soon_total)}` : "All bills settled";
  const sub = overdue && due ? `+${data.due_soon_count} more due within 7 days` : `Paid this month ${fmtMoney(data.paid_this_month)}`;
  const budgetLine = overBudget ? `Over usual monthly ${fmtMoney(data.expected_monthly)} by ${fmtMoney(data.over_budget_by)} (+${data.variance_pct}%)` : null;
  return (
    <Link to="/bills" data-testid="stat-bills-due" data-level={overdue ? "overdue" : overBudget ? "over-budget" : due ? "due" : "ok"} data-budget-alert={overBudget ? "true" : undefined}
          className={`rounded-md p-5 flex flex-col gap-3 border transition-colors hover:opacity-95 ${palette}`}>
      <div className="flex items-center justify-between">
        <span className="overline opacity-80">Rent &amp; bills</span>
        {overdue || overBudget ? <AlertTriangle className="size-4" /> : due ? <Zap className="size-4" /> : <CheckCircle2 className="size-4" />}
      </div>
      <div className="font-display text-xl font-bold leading-tight">{headline}</div>
      <div className={`text-xs ${overdue || overBudget ? "text-red-100" : "opacity-75"}`}>{sub}</div>
      {budgetLine && <div className="text-xs font-semibold flex items-center gap-1" data-testid="stat-bills-budget-alert"><AlertTriangle className="size-3" /> {budgetLine}</div>}
    </Link>
  );
}
